import React, { useState, useCallback } from 'react';
import { calculateDistance } from '../utils/geo';
import { db } from '../firebaseConfig';
import { collection, addDoc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import AddressInput from './AddressInput';
import { toast } from 'react-toastify';

function MeetupForm({ type }) {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: '',
    hasChessSet: 'yes',
    note: '',
    availableUntil: '',
    scheduledStartTime: '',
    meetingDay: 'Saturday',
    meetingTime: '12:00',
    skillLevel: 'any',
    gameType: 'casual',
    isKidFriendly: false,
    isBeginnerFriendly: false,
  });
  const [location, setLocation] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = useCallback((field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleLocationSelect = useCallback((locationData) => {
    setLocation(locationData);
  }, []);



  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !location) {
      toast.error('Please fill out your name and select a location.');
      return;
    }

    setIsSubmitting(true);

    // --- Geofencing Check ---
    if (!navigator.geolocation) {
      toast.error("Your browser doesn't support geolocation, which is required.");
      setIsSubmitting(false);
      return;
    }

    try {
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const userLat = position.coords.latitude;
      const userLng = position.coords.longitude;
      const [targetLatStr, targetLngStr] = location.coordinates.split(',');
      const targetLat = parseFloat(targetLatStr);
      const targetLng = parseFloat(targetLngStr);

      if (isNaN(targetLat) || isNaN(targetLng)) {
        console.error("Invalid target coordinates:", location.coordinates);
        toast.error("Invalid location selected. Please try searching again.");
        setIsSubmitting(false);
        return;
      }

      const distance = calculateDistance(userLat, userLng, targetLat, targetLng);


      if (isNaN(distance) || distance > 50) {
        toast.error(`📍 Spam Protection: You are ${Math.round(distance)} miles away. Must be within 50 miles.`);
        setIsSubmitting(false);
        return;
      }

    } catch (error) {
      console.error("Geofencing error:", error);
      toast.error("Could not verify your location. Please ensure location services are enabled.");
      setIsSubmitting(false);
      return;
    }
    // ------------------------

    const commonData = {
      name: formData.name.trim(),
      address: location.address,
      location: location.coordinates,
      createdAt: Timestamp.now(),
      uid: user.uid,
      photoURL: user.photoURL,
      displayName: user.displayName,
      isKidFriendly: formData.isKidFriendly,
      isBeginnerFriendly: formData.isBeginnerFriendly,
    };

    try {
      if (type === 'club') {
        await addDoc(collection(db, 'clubs'), {
          ...commonData,
          type: 'club',
          meetingDay: formData.meetingDay,
          meetingTime: formData.meetingTime,
          note: formData.note.trim(),
          ownerId: user.uid, // Explicit owner field for clubs
          status: 'pending' // Default status for new clubs
        });
        toast.success("Club registered!");
      } else {
        // Session Data
        const sessionData = {
          ...commonData,
          type, // 'now' or 'scheduled'
          hasChessSet: formData.hasChessSet,
          note: formData.note.trim(),
          participants: [],
          skillLevel: formData.skillLevel,
          gameType: formData.gameType,
        };

        if (type === 'now' && formData.availableUntil) {
          const [hours, minutes] = formData.availableUntil.split(':').map(Number);
          const until = new Date();
          until.setHours(hours, minutes, 0, 0);
          // If time is earlier than now, assume it means tomorrow (e.g. 1 AM when it's 11 PM)
          if (until < new Date()) {
            until.setDate(until.getDate() + 1);
          }
          sessionData.availableUntil = Timestamp.fromDate(until);
        } else if (type === 'scheduled' && formData.scheduledStartTime) {
          sessionData.scheduledStartTime = Timestamp.fromDate(new Date(formData.scheduledStartTime));
        }

        await addDoc(collection(db, 'sessions'), sessionData);
        toast.success(
          type === 'now'
            ? 'You are now visible on the map!'
            : 'Session scheduled! (Switch map filter to "All" or "Scheduled" to view)'
        );
      }

      setFormData({
        name: '',
        hasChessSet: 'yes',
        note: '',
        availableUntil: '',
        scheduledStartTime: '',
        meetingDay: 'Saturday',
        meetingTime: '12:00',
        skillLevel: 'any',
        gameType: 'casual',
        isKidFriendly: false,
        isBeginnerFriendly: false,
      });
      setLocation(null);
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getHeader = () => {
    if (type === 'club') return 'Register a Club';
    return type === 'now' ? 'Mark Yourself Available' : 'Schedule a Meetup';
  };

  return (
    <div className="w-full max-w-md bg-white rounded-xl shadow-lg p-6">
      <h2 className="text-xl font-semibold text-center mb-6">{getHeader()}</h2>
      <form onSubmit={handleSubmit} className="space-y-4">
        <input
          type="text"
          value={formData.name}
          onChange={(e) => updateField('name', e.target.value)}
          className="w-full border border-gray-300 rounded-md px-3 py-2"
          placeholder={type === 'club' ? "Club Name *" : "Your Name *"}
          required
        />

        <AddressInput onLocationSelect={handleLocationSelect} />

        {type !== 'club' && (
          <>
            <div className="grid grid-cols-2 gap-4">
              <select value={formData.skillLevel} onChange={(e) => updateField('skillLevel', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="any">Any Skill Level</option>
                <option value="beginner">Beginner</option>
                <option value="intermediate">Intermediate</option>
                <option value="advanced">Advanced</option>
              </select>
              <select value={formData.gameType} onChange={(e) => updateField('gameType', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
                <option value="casual">Casual</option>
                <option value="blitz">Blitz</option>
                <option value="rapid">Rapid</option>
              </select>
            </div>

            <select value={formData.hasChessSet} onChange={(e) => updateField('hasChessSet', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
              <option value="yes">I have a chess set</option>
              <option value="no">I don't have a chess set</option>
            </select>
          </>
        )}

        {type === 'club' && (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Day</label>
              <select value={formData.meetingDay} onChange={(e) => updateField('meetingDay', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2">
                {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(d => <option key={d} value={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
              <input type="time" value={formData.meetingTime} onChange={(e) => updateField('meetingTime', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" required />
            </div>
          </div>
        )}

        {type === 'now' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Available until</label>
            <input type="time" value={formData.availableUntil} onChange={(e) => updateField('availableUntil', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" />
          </div>
        )}

        {type === 'scheduled' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Scheduled time</label>
            <input type="datetime-local" value={formData.scheduledStartTime} onChange={(e) => updateField('scheduledStartTime', e.target.value)} className="w-full border border-gray-300 rounded-md px-3 py-2" required />
          </div>
        )}

        <textarea
          value={formData.note}
          onChange={(e) => updateField('note', e.target.value)}
          rows={2}
          maxLength={150}
          className="w-full border border-gray-300 rounded-md px-3 py-2"
          placeholder={type === 'club' ? "Club description..." : "Optional notes..."}
        />

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isKidFriendly"
            checked={formData.isKidFriendly}
            onChange={(e) => updateField('isKidFriendly', e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="isKidFriendly" className="ml-2 block text-sm text-gray-900">
            Kid-Friendly
          </label>
        </div>

        <div className="flex items-center">
          <input
            type="checkbox"
            id="isBeginnerFriendly"
            checked={formData.isBeginnerFriendly}
            onChange={(e) => updateField('isBeginnerFriendly', e.target.checked)}
            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
          />
          <label htmlFor="isBeginnerFriendly" className="ml-2 block text-sm text-gray-900">
            Beginner-Friendly
          </label>
        </div>

        <button type="submit" disabled={isSubmitting} className="w-full bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-gray-400">
          {isSubmitting ? 'Saving...' : (type === 'club' ? 'Register Club' : (type === 'now' ? "I'm Open to Play" : "Schedule Game"))}
        </button>
      </form>
    </div>
  );
}

export default MeetupForm;

